import { Auth0Client, createAuth0Client } from '@auth0/auth0-spa-js'
import jsCookie from 'js-cookie'
import { camelCase, isPlainObject } from 'lodash'
import dayjs from '../dayjs'
import auth0Config from './auth0Config'
import LogoutService from './LogoutService'
import ReferrerService from './ReferrerService'
import { Throttler } from '../../utils'
import LoggerService from './LoggerService'
import {
  AUTH0_MAJORCLARITY_COOKIE_NAME,
  AUTH0_PAPER_COOKIE_NAME,
  DEFAULT_EXPIRES_IN_SECONDS,
  PAPER_REFRESH_TOKEN_COOKIE
} from './constants'

import { isAuth0AuthenticatedMajorClarityUser, isFilledStr } from './authenticationHelpers'

let auth0Client: Auth0Client | undefined
export const initializeAuth0 = async (): Promise<void> => {
  auth0Client = await createAuth0Client(auth0Config)
}

const silentLoginThrottler = new Throttler()

export default class AuthenticationService {
  private static readonly CLEVER = 'clever'
  private static readonly CLASSLINK = 'classlink'

  private fromProvider: string | null
  private logoutService: LogoutService
  private referrerService: ReferrerService
  private loggerService: LoggerService

  constructor() {
    this.fromProvider = this.fetchFromProviderFromUrl()
    this.logoutService = new LogoutService().initializeLogoutEventListener()
    this.referrerService = new ReferrerService()
    this.loggerService = new LoggerService()
  }

  public async loginSilently(): Promise<AuthenticationService> {
    if (isAuth0AuthenticatedMajorClarityUser()) {
      this.toggleLoadingSpinner(false)
      console.log('Authenticated MajorClarity user.')

      return this
    }

    if (this.isFromClasslinkPortal() || this.isFromCleverPortal()) {
      this.automaticProviderLogin()

      return this
    }

    if (this.isFromClasslinkPortal()) {
      console.log('Authenticating user with Classlink')
      this.toggleLoadingSpinner(true)
      $('.authentication__button--classlink').click()

      return this
    }

    if (!this.isAuthenticatedPaperUser()) {
      this.toggleLoadingSpinner(false)
      console.log('Login required.')
      this.referrerService.toogleSignInModal(true)
      return this
    }

    try {
      if (!auth0Client) auth0Client = await createAuth0Client(auth0Config)

      silentLoginThrottler
        .forCallback(() => auth0Client.getTokenSilently({ detailedResponse: true }))
        .execute()
        .then((authHash) => {
          this.setAuth0CookieAndRedirectIfValid(authHash)
          return authHash
        })
        .catch((e) => {
          this.referrerService.toogleSignInModal(true)
          throw e
        })
        .finally(() => {
          this.toggleLoadingSpinner(false)
        })
    } catch (e) {
      console.log(e)
      this.loggerService.logRequest(e)
      this.toggleLoadingSpinner(false)
      this.referrerService.toogleSignInModal(true)
      if (e.error !== 'login_required' && e.error !== 'consent_required') return

      const paperAuth0AccessToken = jsCookie.get(AUTH0_PAPER_COOKIE_NAME)
      if (!isFilledStr(paperAuth0AccessToken)) return

      this.setAuth0CookieAndRedirectIfValid({ access_token: paperAuth0AccessToken })
    }

    return this
  }

  private toggleLoadingSpinner(isVisible: boolean) {
    if (isVisible) {
      document.querySelector('#mc-loading')?.classList.remove('hidden')
    } else {
      document.querySelector('#mc-loading')?.classList.add('hidden')
    }
  }

  private setAuth0CookieAndRedirectIfValid(authHash): void {
    if (!isPlainObject(authHash) || !isFilledStr(authHash.access_token)) {
      this.loggerService.logRequest(`Invalid Auth0 Error: ${JSON.stringify(authHash)}`)
      console.log('Invalid authHash provided: ', authHash)
      return
    }

    const { access_token: accessToken, expires_in: expiresIn = DEFAULT_EXPIRES_IN_SECONDS } = authHash
    const expiresInDays = dayjs.duration({ seconds: expiresIn }).asHours() / 24

    console.log(`Attempting to authenticate user with details provided by Auth0`)
    jsCookie.set(AUTH0_MAJORCLARITY_COOKIE_NAME, accessToken, { expires: expiresInDays })
    this.reloadPage()
  }

  private isAuthenticatedPaperUser(): boolean {
    return isFilledStr(jsCookie.get(PAPER_REFRESH_TOKEN_COOKIE))
  }

  private isFromCleverPortal(): boolean {
    return this.fromProvider === AuthenticationService.CLEVER
  }

  private isFromClasslinkPortal(): boolean {
    return this.fromProvider === AuthenticationService.CLASSLINK
  }

  private fetchFromProviderFromUrl(): string {
    const fromProvider = new URLSearchParams(window.location.search).get('from')
    this.removeFromQueryParams()
    return fromProvider
  }

  private removeFromQueryParams(): void {
    const urlParams = new URLSearchParams(window.location.search)
    urlParams.delete('from')
    const urlWithoutFromQueryParam =
      urlParams.size > 0 ? `${window.location.pathname}?${urlParams}` : window.location.pathname
    window.history.replaceState({}, '', urlWithoutFromQueryParam)
  }

  private reloadPage(): void {
    window.location.reload()
  }

  private automaticProviderLogin(): void {
    console.log(`Authenticating user with ${camelCase(this.fromProvider)}`)
    this.toggleLoadingSpinner(true)
    debugger
    $(`.authentication__button--${this.fromProvider}`).click()
  }
}
